// import { CalistActions } from './calist.actions';
import { CA } from 'src/app/shared/entities/ca';
import { createGridDataFeature } from '../../shared/store/grid-data/create-grid-data-feature';
import { createDrilldownFeature } from '../../shared/store/drilldown/create-drilldown-feature';
import {
  createAction,
  createActionGroup,
  createFeature,
  createReducer,
  emptyProps,
  on,
  props,
} from '@ngrx/store';
import { createDeleteItemFeature } from 'src/app/shared/store/delete-item/delete-item-feature';
import { createEditItemFeature } from 'src/app/shared/store/edit-item/create-edit-item-feature';
import { UpdateCaRequestDto } from 'src/app/shared/services/api/dto/update-ca-request-dto';
import { CaRevokeReason } from 'src/app/shared/entities/ca-revoke-reason';

function isCalistUrl(url: string): boolean {
  return url === '/calist' || url.startsWith('/calist?');
}

export const {
  feature: calistFeature,
  actions: CalistDataActions,
  createEffects: createCalistDataEffects,
} = createGridDataFeature<'Calist Data', CA>('Calist Data', {
  routeFilter: route => isCalistUrl(route.url),
});

export const {
  feature: calistDrilldownFeature,
  actions: CalistDrilldownActions,
  createEffects: createCalistDrilldownEffects,
} = createDrilldownFeature<'Calist Drilldown'>('Calist Drilldown', {
  routeFilter: route => isCalistUrl(route.url),
});

export const {
  feature: calistDeleteFeature,
  actions: CalistDeleteActions,
  createEffects: createCalistDeleteEffects,
} = createDeleteItemFeature<'Calist Delete', CA>('Calist Delete');

export const {
  feature: calistEditFeature,
  actions: CalistEditActions,
  createEffects: createCalistEditEffects,
} = createEditItemFeature<'Calist Edit', CA, UpdateCaRequestDto>(
  'Calist Edit',
  {
    routeFilter: route => isCalistUrl(route.url),
  },
);

const CalistRenewActions = createActionGroup({
  source: 'Calist Renew',
  events: {
    renew: props<{ ca: CA }>(),
    renewSuccess: props<{ ca: CA }>(),
    renewFail: props<{ ca: CA; errorMessage: string }>(),
  },
});

export const calistRenewFeature = createFeature({
  name: 'Calist Renew',
  reducer: createReducer<{ renewing: CA | null }>(
    { renewing: null },
    on(CalistRenewActions.renew, (state, { ca }) =>
      !state.renewing ? { renewing: ca } : state,
    ),
    on(CalistRenewActions.renewSuccess, () => ({ renewing: null })),
    on(CalistRenewActions.renewFail, () => ({ renewing: null })),
  ),
});

const CalistRevokeActions = createActionGroup({
  source: 'Calist Revoke',
  events: {
    revoke: props<{
      ca: CA;
      reason: CaRevokeReason;
      revokeLeafCertificates: boolean;
    }>(),
    revokeSuccess: props<{ ca: CA }>(),
    revokeFail: props<{ ca: CA; errorMessage: string }>(),
  },
});

export const calistRevokeFeature = createFeature({
  name: 'Calist Revoke',
  reducer: createReducer<{ revoking: CA | null }>(
    { revoking: null },
    on(CalistRevokeActions.revoke, (state, { ca }) =>
      !state.revoking ? { revoking: ca } : state,
    ),
    on(CalistRevokeActions.revokeSuccess, () => ({ revoking: null })),
    on(CalistRevokeActions.revokeFail, () => ({ revoking: null })),
  ),
});

const CalistGenerateCrlActions = createActionGroup({
  source: 'Calist Generate CRL',
  events: {
    generateCrl: props<{
      ca: CA;
    }>(),
    generateCrlSuccess: props<{ ca: CA; crl: string }>(),
    generateCrlFail: props<{ ca: CA; errorMessage: string }>(),
    generateCrlReset: emptyProps(),
  },
});

export const calistGenerateCrlFeature = createFeature({
  name: 'Calist Generate CRL',
  reducer: createReducer<{
    ca: CA | undefined;
    crl: string | undefined;
    isGenerating: boolean;
  }>(
    { isGenerating: false, ca: undefined, crl: undefined },
    on(CalistGenerateCrlActions.generateCrl, (state, { ca }) =>
      !state.isGenerating ? { isGenerating: true, ca, crl: undefined } : state,
    ),
    on(CalistGenerateCrlActions.generateCrlSuccess, (_, action) => ({
      isGenerating: false,
      ca: action.ca,
      crl: action.crl,
    })),
    on(CalistGenerateCrlActions.generateCrlFail, (_, action) => ({
      isGenerating: false,
      ca: action.ca,
      crl: undefined,
    })),
    on(CalistGenerateCrlActions.generateCrlReset, () => ({
      isGenerating: false,
      ca: undefined,
      crl: undefined,
    })),
  ),
});

const reload = createAction('Calist reload');

export const CalistActions = {
  ...CalistDataActions,
  ...CalistDrilldownActions,
  ...CalistDeleteActions,
  ...CalistEditActions,
  ...CalistRenewActions,
  ...CalistRevokeActions,
  ...CalistGenerateCrlActions,
  reload,
};

export const { selectDrilldownKey: selectCalistDrilldownKey } =
  calistDrilldownFeature;
export const { selectDeleting: selectCalistDeleting } = calistDeleteFeature;
export const { selectSaving, selectEditItemKey: selectCalistEditItemKey } =
  calistEditFeature;
export const calistFeatures = [
  calistFeature,
  calistDrilldownFeature,
  calistDeleteFeature,
  calistRenewFeature,
  calistRevokeFeature,
  calistGenerateCrlFeature,
];
